import { ViewportScroller } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { ButtonType, StepIndexChangeEvent } from '../../../interfaces';
import { FormUtil } from '../../../util';
import { StepComponent } from '../step/step.component';

@Component({
  selector: 'un-steps',
  templateUrl: './steps.component.html',
  styleUrls: ['./steps.component.less'],
  standalone: false,
})
export class StepsComponent
  implements AfterContentInit, OnChanges, AfterViewInit
{
  @Input() index = 0;
  @Input() scrollPx = 150;
  @Input() requireVisit = false;
  @Input() isLoading: boolean;
  @Input() valid = true;
  @Input() allowSave = true;
  @Input() editMode?: boolean;
  @Input() status?: ButtonType;
  @Input() submitBtnText = $localize`Submit`;
  @Input() hideButtons: boolean;
  @Output() indexChange = new EventEmitter<StepIndexChangeEvent>();
  @Output() save = new EventEmitter<void>();
  @Output() submitClick = new EventEmitter<void>();
  @ContentChildren(StepComponent) steps: QueryList<StepComponent>;

  @ViewChild('stepHeader', {
    static: true,
  })
  stepHeader: ElementRef;

  @ViewChildren('stepTitles', {
    read: ElementRef,
  })
  stepTitles: QueryList<ElementRef>;
  private dead$ = new Subject();

  constructor(
    private cdr: ChangeDetectorRef,
    private viewScroll: ViewportScroller
  ) {}

  get isFirstStep() {
    return this.index === 0;
  }

  get isLastStep() {
    return this.index === (this.steps || []).length - 1;
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['index'] || !this.steps) return;
    const { previousValue: prev, currentValue: curr } = changes['index'];
    const prevStep = this.steps.get(prev) as StepComponent;
    prevStep.selected = false;
    const step = this.steps.get(curr);
    if (step) step.selected = true;
    prevStep.stepChanged.emit({ key: prevStep.key, index: prev });
    this.cdr.detectChanges();
  }

  ngAfterContentInit(): void {
    const step = this.steps.get(this.index);
    if (step && !this.requireVisit) step.selected = true;
    // if a step becomes invalid, mark all the steps following it as NOT visited
    this.steps.forEach((step, index) =>
      step.validChange
        .pipe(
          takeUntil(this.dead$),
          filter((v) => !v)
        )
        .subscribe(() => {
          for (let i = index + 1; i < this.steps.length; i++) {
            const stepI = this.steps.get(i);
            if (stepI) {
              stepI.visitCount++;
              stepI.visited = false;
            }
          }
        })
    );
  }

  isStepEnabled(index: number) {
    if (!this.requireVisit) return true;
    return this.steps
      .toArray()
      .slice(0, index)
      .every((s) => (s ? s.valid : true) && (!this.requireVisit || s.visited));
  }

  setSelectedIndex(index: number) {
    const prev = this.index;
    const prevStep = this.steps.get(prev);
    this.updateStepFormValidity(prevStep);
    const currStep = this.steps.get(index);

    const step = this.stepTitles.get(index - 1);
    if (step)
      this.stepHeader.nativeElement.scrollLeft = step.nativeElement.offsetLeft;

    this.isStepEnabled(index) &&
      index >= 0 &&
      index < this.steps.length &&
      this.indexChange.emit({
        prevIndex: prev,
        nextIndex: index,
        prevKey: prevStep?.key,
        nextKey: currStep?.key,
      });
    this.viewScroll.scrollToPosition([0, 0]);
  }

  nextStep() {
    this.setSelectedIndex(this.index + 1);
  }

  updateStepFormValidity(step?: StepComponent) {
    if (step && step.form) {
      const form = step.form;
      FormUtil.updateFormValidity(form);
    }
  }

  prevStep() {
    this.setSelectedIndex(this.index - 1);
  }

  submitForm() {
    this.editMode = true;
    for (const step of this.steps) this.updateStepFormValidity(step);
    if (this.isStepEnabled((this.steps || []).length - 1))
      this.submitClick.emit();
  }
}
